import TimeUtils from "../../js/util/TimeUtils.js";
import LicenseUtils from "../../js/util/LicenseUtils.js";

/**
 * A banner shown on every page while the license is expired, expiring soon, or invalid. It reports the
 * status of the customer license (not evaluation) whenever one exists. When the license is fine (or there's
 * none at all) nothing is shown.
 *
 * Only the "expiring soon" warning is dismissable.
 * Dismissing hides it for 5 minutes, after which it reappears if the license still needs attention.
 * An expired or invalid license is a blocking problem, so its banner has no close button and always shows.
 */
export default class LicenseExpiryBannerCmp {
	static #DISMISSED_UNTIL_STORAGE_KEY = 'license-expiry-banner-dismissed-until';
	static #DISMISS_DURATION_MS = 5 * 60 * 1000;

	#userSessionRepository;
	#securityContext;

	/**
	 * @param {UserSessionRepository} userSessionRepository
	 * @param {SecurityContext} securityContext
	 */
	constructor(userSessionRepository, securityContext) {
		this.#userSessionRepository = userSessionRepository;
		this.#securityContext = securityContext;
	}

	/**
	 * Prepends the expiry banner to the parent element when the license is expired/expiring/invalid; otherwise
	 * does nothing. The "expiring soon" warning is skipped if the user dismissed it in the last 5 minutes.
	 * @param {HTMLElement} parentElement
	 */
	async init(parentElement = document.body) {
		if (!this.#securityContext.isAuthenticated())
			return;
		const resp = await this.#userSessionRepository.getLicenseStatus();
		if (!resp.expiry)
			return;
		const expiry = LicenseUtils.deriveExpiry(resp.expiry);
		const isAdmin = this.#securityContext.getCurrentUser().isAdmin();
		const banner = LicenseExpiryBannerCmp.#buildBanner(expiry, isAdmin);
		if (!banner)
			return;
		const dismissable = expiry.status === 'EXPIRING_SOON';
		if (dismissable && LicenseExpiryBannerCmp.#isDismissed())
			return;
		parentElement.prepend(banner);
		LicenseExpiryBannerCmp.#position(banner);
		// The banner's height changes when the viewport narrows and its text wraps, so re-position on resize.
		const resizeObserver = typeof ResizeObserver !== 'undefined'
			? new ResizeObserver(() => LicenseExpiryBannerCmp.#position(banner))
			: null;
		resizeObserver?.observe(banner);
		if (dismissable)
			banner.querySelector('.license-expiry-banner__close').addEventListener('click', () => {
				resizeObserver?.disconnect?.();
				banner.remove();
				document.body.style.paddingTop = '';
				LicenseExpiryBannerCmp.#dismiss();
			});
	}

	/** @returns {boolean} whether the banner was dismissed within the last 5 minutes */
	static #isDismissed() {
		const dismissedUntil = Number(window.localStorage.getItem(LicenseExpiryBannerCmp.#DISMISSED_UNTIL_STORAGE_KEY));
		return dismissedUntil > Date.now();
	}

	static #dismiss() {
		const dismissedUntil = Date.now() + LicenseExpiryBannerCmp.#DISMISS_DURATION_MS;
		window.localStorage.setItem(LicenseExpiryBannerCmp.#DISMISSED_UNTIL_STORAGE_KEY, String(dismissedUntil));
	}

	/**
	 * Pins the fixed banner just below the fixed 4rem page header and pads the body by its height so page
	 * content isn't covered.
	 * @param {HTMLElement} banner
	 */
	static #position(banner) {
		banner.style.top = '4rem';
		document.body.style.paddingTop = banner.offsetHeight + 'px';
	}

	/**
	 * @param {{status: string, daysLeft: number, reason: ?string}} expiry
	 * @param {boolean} isAdmin
	 * @returns {?HTMLElement} the banner element, or null when the license needs no warning
	 */
	static #buildBanner(expiry, isAdmin) {
		const message = LicenseExpiryBannerCmp.#message(expiry, isAdmin);
		if (!message)
			return null;
		// Only the "expiring soon" heads-up is dismissable; an expired/invalid license is blocking, so no close.
		const dismissable = expiry.status === 'EXPIRING_SOON';
		const severity = dismissable ? 'warning' : 'error';
		const banner = document.createElement('div');
		banner.className = `license-expiry-banner license-expiry-banner--${severity}`;
		banner.setAttribute('role', 'alert');
		banner.innerHTML = `
			<span class="material-symbols-outlined license-expiry-banner__icon" aria-hidden="true">warning</span>
			<span class="license-expiry-banner__text"></span>
			${dismissable ? `<button type="button" class="license-expiry-banner__close" aria-label="Close">
				<span class="material-symbols-outlined" aria-hidden="true">close</span>
			</button>` : ''}`;
		banner.querySelector('.license-expiry-banner__text').textContent = message;
		return banner;
	}

	/** @returns {?string} the banner text, or null for OK/NONE (no warning needed) */
	static #message(expiry, isAdmin) {
		const contactAdmin = 'Contact your administrator.';
		switch (expiry.status) {
			case 'EXPIRED':
				return isAdmin
					? `License expired ${TimeUtils.daysAgo(expiry.daysLeft)}. Renew it to restore full access.`
					: `License has expired. ${contactAdmin}`;
			case 'EXPIRING_SOON':
				return isAdmin
					? `License expires ${TimeUtils.withinDays(expiry.daysLeft)}. Renew it to avoid an interruption.`
					: `License expires ${TimeUtils.withinDays(expiry.daysLeft)}. ${contactAdmin}`;
			case 'INVALID':
				return isAdmin
					? `License is invalid${LicenseExpiryBannerCmp.#reason(expiry.reason)}. Upload a valid license to restore full access.`
					: `There is a problem with the license. ${contactAdmin}`;
			default: // OK, NONE
				return null;
		}
	}

	/** @param {?string} reason */
	static #reason(reason) {
		return reason ? ` (${reason})` : '';
	}
}
